import React from "react";
import ModalForm from "@ui/Modal/Form";
import t from "../../../../i18n";
import InputV2 from "@ui/Input/InputV2";
import useInput from "@ui/lib/hooks/useInput";
import { validateUrl } from "@ui/lib/urlTools";
import { isLeft } from "fp-ts/lib/Either";
import { medium, veryLong } from "diagnostics-lib/io";
import * as io from "io-ts";
import { usePost } from "../../../../api/useCall";
import type { ExternalLink } from "../../../../api/useExternalLinks";

interface ModalFormUpsertLinkProps {
  externalLink?: ExternalLink;
  onRequestClose: () => void;
}

export const ModalFormUpsertLink = ({ externalLink, onRequestClose }: ModalFormUpsertLinkProps) => {
  const upsertExternalLink = usePost("/externalLinks");

  const {
    defaultValue: frLinkNameDefaultValue,
    value: frLinkName,
    onChange: onChangeFrLinkName,
    dirty: frLinkNameDirty,
  } = useInput(externalLink?.frName);
  const {
    defaultValue: enLinkNameDefaultValue,
    value: enLinkName,
    onChange: onChangeEnLinkName,
    dirty: enLinkNameDirty,
  } = useInput(externalLink?.enName);
  const {
    defaultValue: urlDefaultValue,
    value: url,
    onChange: onChangeURL,
    dirty: urlDirty,
  } = useInput(externalLink?.url);

  const errorFrLinkName = React.useMemo(() => {
    if (frLinkNameDirty && frLinkName.trim().length === 0)
      return t("app.administration.application.link.modal.form.edit.link_name.error.empty_field");
    if (frLinkNameDirty && isLeft(medium(io.string).decode(frLinkName.trim()))) {
      return t(
        "app.administration.application.link.modal.form.input.error.cannot_exceed_250_characters",
      );
    }
    return;
  }, [frLinkNameDirty, frLinkName]);
  const errorEnLinkName = React.useMemo(() => {
    if (enLinkNameDirty && enLinkName.trim().length === 0)
      return t("app.administration.application.link.modal.form.edit.link_name.error.empty_field");

    if (enLinkNameDirty && isLeft(medium(io.string).decode(enLinkName.trim()))) {
      return t(
        "app.administration.application.link.modal.form.input.error.cannot_exceed_250_characters",
      );
    }
    return;
  }, [enLinkNameDirty, enLinkName]);
  const errorURL = React.useMemo(() => {
    if (urlDirty && url.trim().length === 0)
      return t("app.administration.application.link.modal.form.edit.url.error.empty_field");
    if (urlDirty && validateUrl(url) === false) return t("common.link.error.invalid_url");
    if (urlDirty && isLeft(veryLong(io.string).decode(url.trim())))
      return t(
        "app.administration.application.link.modal.form.input.url.error.cannot_exceed_4000_characters",
      );
    return;
  }, [url, urlDirty]);

  const dirty =
    frLinkName !== "" &&
    enLinkName !== "" &&
    url !== "" &&
    (frLinkNameDirty || enLinkNameDirty || urlDirty) &&
    errorFrLinkName === undefined &&
    errorEnLinkName === undefined &&
    errorURL === undefined;

  const handleSubmit = React.useCallback(async () => {
    await upsertExternalLink({
      body: {
        id: externalLink?.id,
        enName: enLinkName,
        frName: frLinkName,
        url,
      },
    });
    onRequestClose();
    return true;
  }, [upsertExternalLink, externalLink, enLinkName, frLinkName, url, onRequestClose]);

  return (
    <ModalForm
      onSubmit={dirty ? handleSubmit : false}
      onRequestClose={onRequestClose}
      okButtonName={externalLink ? t("modalForm.button.name.edit") : t("modalForm.button.name.add")}
    >
      <InputV2
        type="text"
        defaultValue={frLinkNameDefaultValue}
        label={t("app.administration.application.link.modal.form.text_fr.label")}
        value={frLinkName}
        onChange={onChangeFrLinkName}
        error={errorFrLinkName}
        revertable={Boolean(frLinkNameDefaultValue) && frLinkNameDefaultValue !== frLinkName}
      />
      <InputV2
        type="text"
        defaultValue={enLinkNameDefaultValue}
        label={t("app.administration.application.link.modal.form.text_en.label")}
        value={enLinkName}
        onChange={onChangeEnLinkName}
        error={errorEnLinkName}
        revertable={Boolean(enLinkNameDefaultValue) && enLinkNameDefaultValue !== enLinkName}
      />
      <InputV2
        type="text"
        defaultValue={urlDefaultValue}
        label="URL"
        placeholder="https://"
        value={url}
        onChange={onChangeURL}
        error={errorURL}
        revertable={Boolean(urlDefaultValue) && urlDefaultValue !== url}
      />
    </ModalForm>
  );
};

//#region CSS
//#endregion
